import { ISpeedometerCardProps, SpeedometerCard } from '@web_opxp/components';
import { useTranslation } from '@web_opxp/features';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { getExerciseIconSrc } from '../../assets';
import { SKELETON_CLASSNAME, SKELETON_DUMMYTEXT } from '../../base/constants';
import { isChallengeWorkoutId } from '../../base/utils';
import { ExerciseBaseRoute } from '../../exercise/Exercise';
import './ExerciseCard.scss';

export interface IExerciseCardProps {
  // name of the exercise for the workout (defines title and icon)
  exerciseId: string | null;
  // show checkmark instead of the icon if completed and other styles
  completed?: boolean;
  // histogram comparison value for given workout
  comparison: number | null;
  // locked flag
  locked?: boolean;
  // workout id
  workoutId: number;
  // optional class added by parent
  className?: string;
  // optional id
  id?: string;
  // additional content at the bottom of the card
  subtitle?: any;
  // shows the index of session array
  sessionArrayIndex?: number;
}

const ExerciseCard: React.FC<IExerciseCardProps> = ({
  exerciseId,
  comparison,
  locked,
  completed,
  workoutId,
  className,
  id,
  subtitle,
  sessionArrayIndex,
}) => {
  const { t } = useTranslation();
  const isLoading = !exerciseId || !workoutId || comparison === null;
  const isChallenge = isChallengeWorkoutId(workoutId);
  const challengePostfix = isChallenge ? 'Challenge' : '';
  const exerciseTitle = isLoading ? SKELETON_DUMMYTEXT : t(`common.exercises:Title.${exerciseId}${challengePostfix}`);

  const percent = Math.round(Number(comparison ?? 0));
  const title = `${exerciseTitle} ${comparison === null ? '--' : percent}%`;
  const iconSrc = isLoading ? '' : getExerciseIconSrc(exerciseId, completed, locked);
  const cardProps: ISpeedometerCardProps = {
    gaugeMaxLabel: t('Exercise.Skill.Max'),
    gaugeMinLabel: t('Exercise.Skill.Min'),
    iconSrc,
    title,
    value: percent,
    className: isLoading ? SKELETON_CLASSNAME : '',
    id,
    subtitle,
  };
  const card = <SpeedometerCard {...cardProps} />;

  let cardClass = 'exercise-card';
  if (className) cardClass = cardClass.concat(` ${className}`);
  if (completed) cardClass = cardClass.concat(' completed');
  if (locked) cardClass = cardClass.concat(' locked');

  let exerciseLink = '';

  if (exerciseId) {
    exerciseLink = `${ExerciseBaseRoute}/${exerciseId.toLowerCase()}`;
    if (isChallenge) exerciseLink = exerciseLink.concat('challenge');
  }

  return isLoading || completed ? ( // remove link if 'completed' is defined and is true
    <div data-testid="exercise-card" className={cardClass}>
      {card}
    </div>
  ) : (
    <Link
      data-testid="exercise-card"
      to={{
        pathname: exerciseLink,
        state: { index: sessionArrayIndex },
      }}
      className={cardClass}>
      {card}
    </Link>
  );
};

export default ExerciseCard;
